import type { NonEmptyArray } from './utils';
import type { Signature } from '@mls-wallet/database';
import type { Account, Asset } from './account';
import type { NetworkId, NetworkType } from './network';
import type { PolicyType } from './policy';
import type { OperationType } from '@mls-wallet/transaction-decoder';
import type { Address, Hex } from 'viem';
import { z } from 'zod';
import {
  AcctTxSignableDecision,
  OrgOpSignableDecision,
  accountTransactionDecisionSchema,
} from './decision';
import { ChecksumAddress } from './address';
import type {
  DomainInvalidReason,
  InvalidProposalValidity,
} from './proposalValidity';

/**
 * Signature status for the API transaction approval workflow.
 * See OpenAPI spec for full documentation.
 */
export type ApiSignatureStatus =
  | 'pendingInitiatorSignature'
  | 'pendingReviewSignatures'
  | 'approvalReady'
  | 'rejectionReady'
  | 'approvalAndRejectionReady'
  | 'expired';

/**
 * Execution lifecycle state for API transaction responses.
 */
export type ApiExecutionStatus =
  | 'processing'
  | 'completed'
  | 'rejected'
  | 'failed'
  | 'blocked'
  | null;

export const accountTransactionExecutionOutcomeSchema = z.enum([
  'COMPLETED',
  'REVERTED',
  'BLOCKED',
  'PARTIALLY_EXECUTED',
]);

type SignableStatus = 'pending' | 'approved' | 'rejected';

// Casing is mixed up because these values were split into multiple enums originally, so values that may be in DB were preserved
export enum SignableType {
  AccountTransactionBatch = 'ACCOUNT_TRANSACTION_BATCH',
  TokenTransfer = 'TOKEN_TRANSFER',
  ContractInteraction = 'CONTRACT_INTERACTION',
  OrganizationOperation = 'organization_operation',
  InvalidOrganizationOperation = 'invalid_organization_operation',
}

interface Policy {
  id: string;
  /** Auto-approval rejection uses an initiator signature; manual rejection uses reviewer signatures. */
  type: PolicyType;
  // On-chain ID (UUID) - converted to uint256 at contract boundary
  onchainId: string;
  name: string;
  description: string;
  requiredApprovals: number;
  totalApprovers: number;
}

export interface Member {
  id: string;
  name: string;
  avatar?: string;
}

export interface FunctionParameter {
  name: string;
  type: string;
  value: string;
}

/**
 * Decoded function data suitable for database storage.
 * Contains the function name and arguments with full metadata.
 */
export interface DecodedFunctionData {
  functionName: string;
  args: FunctionParameter[];
}

/**
 * ABI parameter input metadata (name and type without value).
 * Used when passing function parameter metadata from frontend to backend.
 */
export const abiParameterInputSchema = z.object({
  name: z.string(),
  type: z.string(),
});

export type AbiParameterInput = z.infer<typeof abiParameterInputSchema>;

/** One child transaction; its source account and network belong to the parent. */
export type AccountTransactionChildDraft = {
  to: string;
  value: string;
  data: string;
  policyId: string;
  functionInputs?: AbiParameterInput[];
  functionName?: string;
  functionSignature?: string;
};

/** Unsaved parent transaction; every child shares its source account and network. */
export type AccountTransactionDraft = {
  multichainAccountId: string;
  networkId: NetworkType;
  description: string;
  // The first child is required; the rest tuple permits additional children for imported drafts.
  // [T, ...T[]] is a non-empty array: [child] is valid, but [] is not.
  children: NonEmptyArray<AccountTransactionChildDraft>;
};

/**
 * HTTP response for transaction creation, including child IDs for signing.
 * Shared by the backend route and web hook so both use the same API contract.
 */
export interface CreateTransactionResponse {
  success: boolean;
  transaction: {
    id: string;
    // Creation returns identities and order; the read API supplies the full child details.
    children: NonEmptyArray<
      Pick<AccountTransactionSigningChild, 'id' | 'position'>
    >;
  };
}

interface BaseTransactionOrOperation {
  id: string;
  salt: string;
  /** Expiration timestamp is an integer epoch time (seconds) */
  expirationTimestamp: number;
  signableHash?: string;
  dateProposed: string; // ISO 8601 date string
  dateExecuted?: string; // ISO 8601 date string
  initiator: Member;
  rejectionReason?: string;
  organizationAddress: Address;
}

/** Metadata shared by every independently authorized child of a transaction. */
export interface AccountTransactionContext
  extends Omit<BaseTransactionOrOperation, 'salt' | 'signableHash'> {
  /** Whether this transaction was invalidated by a policy change */
  invalidated: boolean;
  /** Whether the current user has dismissed this invalidated transaction (optional, only present when querying with userId) */
  dismissedByCurrentUser?: boolean;
  account: Account;
  networkId: NetworkId;
  description: string;
}

interface AccountTransactionChildBase {
  id: string;
  salt: string;
  signableHash?: string;
  policy: Policy;
  /** Initiator signature would be undefined after the tx is created but before it's signed by the initiator */
  initiatorSignature: Signature | undefined;
  pendingApprovers: Member[];
  /** Eligible members who have not rejected: policy initiators for auto-approval, approvers for manual approval. */
  pendingRejectors: Member[];
  approvedMembers: Member[];
  rejectedMembers: Member[];
  approvals: Signature[];
  rejections: Signature[];
  // Raw transaction data
  toAddress: Address;
  data: string;
  value: string;
  // Optional server-enriched policy limitation status for queued execution UX.
  overPolicyLimit?: boolean;
  policyLimitWindowStart?: string;
  policyLimitWindowEnd?: string;
  policyLimitThresholdRaw?: string;
  policyLimitUsedAmountRaw?: string;
  policyLimitRemainingAmountRaw?: string;
}

export const getContractInteractionName = (functionName: string) => {
  // Convert camelCase to readable string
  // e.g., "transferFrom" -> "Transfer From", "setApprovalForAll" -> "Set Approval For All"
  return functionName
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
    .trim(); // Remove any leading/trailing whitespace
};

// ============================================================================
// Decoded Transaction Types (from raw transaction data)
// ============================================================================
// These are the minimal types returned from decoding raw transaction data.
// They differ from the full transaction types below which include status,
// approvals, initiator, and other metadata.

/**
 * Decoded token transfer from raw transaction data
 */
interface DecodedTokenTransfer {
  type: SignableType.TokenTransfer;
  tokenAddress: string;
  /** Raw token amount in base units (stringified bigint) */
  rawAmount: string;
  /** Display-friendly token amount (decimal-adjusted) */
  displayAmount: string;
  destinationAddress: ChecksumAddress;
}

/**
 * Decoded contract interaction from raw transaction data
 */
interface DecodedContractInteraction {
  type: SignableType.ContractInteraction;
  contractAddress: ChecksumAddress;
  functionName?: string | undefined;
}

/**
 * Result of decoding raw transaction data
 */
export type DecodedTransaction =
  | DecodedTokenTransfer
  | DecodedContractInteraction;

// ============================================================================
// Full Transaction Types (with metadata and approvals)
// ============================================================================

export interface TokenTransferTransactionChild
  extends AccountTransactionChildBase {
  type: SignableType.TokenTransfer;
  destination: ChecksumAddress;
  destinationName?: string | null;
  asset: Asset;
  /** Display-friendly token amount */
  displayAmount: string;
  /** Raw token amount with full decimals - use this in mobile app for validation */
  rawAmount: string;
}

export interface ContractInteractionTransactionChild
  extends AccountTransactionChildBase {
  type: SignableType.ContractInteraction;
  functionName: string;
  functionParameters: FunctionParameter[];
  /** Address book label for the contract address, if one exists in the org. */
  contractAddressName?: string | null;
  /** Contract name from block explorer (e.g., "UniswapV3Router"). Null if unverified or unavailable. */
  contractName: string | null;
}

/**
 * Special enum for determining what text to display in the mobile app
 * The reason we need an enum separate from OperationType is OperationType needs to match the contracts exactly.
 * (OperationType enum needs to define all the values in the same order in JS and Solidity, bc enum options are given a uint8 value, so the integers need to be the same in both systems)
 * And there isn't as much granularity for types like AddMember and RemoveMember
 */
export enum OperationDisplayType {
  // Multichain account proposals
  DeployAccount,
  // Org member proposals (driven by org member proposal type)
  AddMember,
  RemoveMember,
  // Group proposals (driven by org member proposal type)
  UpdateGroup,
  AddGroup,
  RemoveGroup,
  // Admin change proposals (driven by set of admin user proposals)
  AddAdmin,
  AddAdmins,
  RemoveAdmin,
  RemoveAdmins,
  ModifyAdmins,
  // Policy proposals (driven by set of policy change proposals)
  AddPolicy,
  AddPolicies,
  RemovePolicy,
  RemovePolicies,
  ModifyPolicy,
  ModifyPolicies,
  // Disaster recovery proposals
  InitiateTransactionRecovery,
  FinalizeTransactionRecovery,
  CancelTransactionRecovery,
}
export type NetworksOperationData = {
  networkId: NetworkId;
  operationData: Hex;
};
/** Used to give the mobile app displayable parameter per operation type */
export interface OperationDisplayParameter {
  name: string;
  value: string;
}
interface OrganizationOperation extends BaseTransactionOrOperation {
  type: SignableType.OrganizationOperation;
  functionName: string;
  operationDisplayParameters: OperationDisplayParameter[]; // Pre-formatted parameters for easy display in the app
  operationDisplayType: OperationDisplayType;
  operationType: OperationType;
  networksOperationData: NetworksOperationData[];
}

/**
 * Reject-only mobile signable for invalid organization-operation proposals.
 * It still carries the canonical
 * operation tuple so rejection signatures burn the same nonce as the original
 * proposal.
 */
export interface InvalidOrganizationOperationSignable
  extends BaseTransactionOrOperation {
  type: SignableType.InvalidOrganizationOperation;
  validity: InvalidProposalValidity;
  domainInvalidReason?: DomainInvalidReason;
  decision: Extract<OrgOpSignableDecision, 'REJECT'>;
  status: SignableStatus;
  operationType: OperationType;
  networksOperationData: NetworksOperationData[];
}

/** Account/network and lifecycle metadata belong to the parent, never a child. */
export type AccountTransactionChild =
  | TokenTransferTransactionChild
  | ContractInteractionTransactionChild;

/** Every saved transaction is a parent containing at least one ordered child. */
export type AccountTransaction = AccountTransactionContext & {
  children: NonEmptyArray<
    AccountTransactionChild & { signatureStatus: ApiSignatureStatus }
  >;
  signatureStatus: ApiSignatureStatus;
  executionStatus: ApiExecutionStatus;
};

// Existing child display, policy validation and typed-data encoders need parent context.
// These adapters describe one child; creation and read contracts always use the parent.
export type AccountTransactionChildBaseWithContext = AccountTransactionContext &
  AccountTransactionChildBase;
export type TokenTransferChildWithContext = AccountTransactionContext &
  TokenTransferTransactionChild;
export type ContractInteractionChildWithContext = AccountTransactionContext &
  ContractInteractionTransactionChild;
export type AccountTransactionChildWithContext = AccountTransactionContext &
  AccountTransactionChild;

/**
 * Whether a contract interaction was stored without decoded function metadata.
 *
 * The backend preserves opaque contract calls with a blank function name, so
 * clients should render their raw calldata instead of an empty function label.
 * This accepts account-transaction signables as well because they extend the
 * same transaction shape.
 */
export function isUndecodedContractInteraction(
  transaction: AccountTransactionChild
): boolean {
  return (
    transaction.type === SignableType.ContractInteraction &&
    transaction.functionName.trim().length === 0
  );
}

export type AccountTransactionChildWithDerivedStatuses =
  AccountTransactionChildWithContext &
    Pick<AccountTransaction, 'signatureStatus' | 'executionStatus'>;

/** Expand parent context only for existing single-transaction presentation/validation adapters. */
export function transactionChildWithContext(
  batch: AccountTransaction,
  child: AccountTransaction['children'][number]
): AccountTransactionChildWithDerivedStatuses {
  const { children: _children, ...context } = batch;
  return { ...context, ...child };
}

/** Each parent decision requires that decision's quorum on every child. */
export function deriveBatchSignatureStatus(
  statuses: ApiSignatureStatus[]
): ApiSignatureStatus {
  const first = statuses[0];
  if (!first) throw new Error('A transaction must have at least one child');
  if (statuses.length === 1) return first;
  if (statuses.includes('expired')) return 'expired';
  if (statuses.includes('pendingInitiatorSignature'))
    return 'pendingInitiatorSignature';
  const approvalReady = statuses.every(
    (status) =>
      status === 'approvalReady' || status === 'approvalAndRejectionReady'
  );
  const rejectionReady = statuses.every(
    (status) =>
      status === 'rejectionReady' || status === 'approvalAndRejectionReady'
  );
  if (approvalReady && rejectionReady) return 'approvalAndRejectionReady';
  if (approvalReady) return 'approvalReady';
  if (rejectionReady) return 'rejectionReady';
  return 'pendingReviewSignatures';
}

type OrgOpSignableTemplateType<T> = T & {
  decision: OrgOpSignableDecision;
  status: SignableStatus;
};
export type AcctTxSignableTemplateType<T> = T & {
  /** Groups independently signed child payloads into one mobile confirmation. */
  // Every account transaction has a parent, including a single-child transaction.
  batch: { id: string; childCount: number; position: number };
  decision: AcctTxSignableDecision;
  status: SignableStatus;
};

export type ContractInteractionTransactionSignable =
  AcctTxSignableTemplateType<ContractInteractionChildWithContext>;
export type TokenTransferTransactionSignable =
  AcctTxSignableTemplateType<TokenTransferChildWithContext>;
export type AccountTransactionChildSignable =
  AcctTxSignableTemplateType<AccountTransactionChildWithContext>;
export type OrganizationOperationSignable =
  OrgOpSignableTemplateType<OrganizationOperation>;
/** Payloads consumed by existing per-child signing and policy-validation adapters. */
export type SigningPayload =
  | AccountTransactionChildSignable
  | OrganizationOperationSignable
  | InvalidOrganizationOperationSignable;

/** Child payload and order within a parent signing action. */
export type AccountTransactionSigningChild = AccountTransactionChild & {
  position: number;
};

/**
 * A mobile signing action owns parent context and only this member's eligible children.
 * @public Shared signing contract; client activation is delivered separately.
 */
export type AccountTransactionSignable = AccountTransactionContext & {
  type: SignableType.AccountTransactionBatch;
  decision: AcctTxSignableDecision;
  status: SignableStatus;
  childCount: number;
  children: NonEmptyArray<AccountTransactionSigningChild>;
};

/**
 * The v2 mobile feed returns account transactions as parent signing actions.
 * @public Shared mobile feed contract.
 */
export type Signable =
  | AccountTransactionSignable
  | OrganizationOperationSignable
  | InvalidOrganizationOperationSignable;

/**
 * Expand parent context only at the existing child display and typed-data boundary.
 * @public Adapter for consumers of the shared mobile signing contract.
 */
export function signingChildWithContext(
  batch: AccountTransactionSignable,
  child: AccountTransactionSignable['children'][number]
): AccountTransactionChildSignable {
  const { children: _children, childCount, type: _type, ...context } = batch;
  return {
    ...context,
    ...child,
    batch: { id: batch.id, childCount, position: child.position },
  };
}

/**
 * Validate the HTTP body and derive the mobile request type from the same fields.
 * The signing use case separately verifies child eligibility and signature bytes.
 * @public Shared request validation, published before signing is activated.
 */
export const confirmAccountTransactionBatchSchema = z.object({
  decision: accountTransactionDecisionSchema,
  signatures: z
    .array(z.object({ childId: z.uuid(), signature: z.string().min(1) }))
    .min(1),
});
/** @public Request body for mobile batch signature confirmation. */
export type ConfirmAccountTransactionBatchRequest = z.infer<
  typeof confirmAccountTransactionBatchSchema
>;

/**
 * Cancellation uses the same decision contract without uploading signatures.
 * @public Shared request validation, published before signing is activated.
 */
export const cancelAccountTransactionBatchSchema =
  confirmAccountTransactionBatchSchema.pick({ decision: true });
/** @public Request body for cancelling a pending mobile signing action. */
export type CancelAccountTransactionBatchRequest = z.infer<
  typeof cancelAccountTransactionBatchSchema
>;
